import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../../hooks/useAuth"
import { clearPaymentRequired } from "../../store/subscription/actions"
import { isSubscriptionAllowedPath } from "../../constants/subscriptionAllowedRoutes"
import SubscriptionLockedCard, { ADMIN_DECISION_MESSAGE } from "./SubscriptionLockedCard"

// NOT: EXPIRED abonelik route bloğu artık SubscriptionGate tarafından
// (blur + kilit ekranı, sayfa mount edilmeden) yönetilir. Bu overlay yalnızca
// aksiyon-tetikli senaryolarda çalışır:
//   - 402 Payment Required (paket limiti aşımı / abonelik yok)
//   - cari / kullanıcı limiti aşımı (kapatılabilir; paket düşüşünde mevcut kayıtlar
//     çalışmaya devam eder, yalnız yenisi eklenemez — panel kilitlenmez)
// Bunlar izinli sayfalarda bir işlem sırasında oluşabildiği için ayrı kalır.
const getOverlayContent = (currentStatus, paymentRequired) => {
  // 402 Payment Required — backend'den gelen mesajla overlay göster
  if (paymentRequired) {
    return {
      icon: "bx-package",
      title: paymentRequired.title || "Paket Yükseltme Gerekli",
      message: paymentRequired.message || "Bu işlemi gerçekleştirebilmek için paketinizi yükseltmeniz gerekmektedir.",
      cta: "Paketleri İncele",
    }
  }

  if (!currentStatus) return null

  const { maxAccounts, maxUsers, maxAccountUsers, currentAccounts, currentUsers, currentAccountUsers } = currentStatus

  // Cari limiti aşılmış
  if (maxAccounts != null && currentAccounts != null && currentAccounts > maxAccounts) {
    return {
      icon: "bx-user-x",
      title: "Cari Limitiniz Aşıldı",
      message: `Mevcut paketiniz en fazla ${maxAccounts} cari hesap destekliyor, ancak ${currentAccounts} cari hesabınız bulunuyor. Yeni cari ekleyebilmek için paketinizi yükseltmeniz gerekmektedir.`,
      cta: "Paketi Yükseltin",
    }
  }

  // Alt kullanıcı (firma personeli) limiti aşılmış
  if (maxUsers != null && currentUsers != null && currentUsers > maxUsers) {
    return {
      icon: "bx-group",
      title: "Alt Kullanıcı Limitiniz Aşıldı",
      message: `Mevcut paketiniz en fazla ${maxUsers} alt kullanıcı destekliyor, ancak ${currentUsers} alt kullanıcınız bulunuyor. Yeni kullanıcı ekleyebilmek için paketinizi yükseltmeniz gerekmektedir.`,
      cta: "Paketi Yükseltin",
    }
  }

  // Cari kullanıcı limiti aşılmış — alt kullanıcıdan ayrı havuz
  if (maxAccountUsers != null && currentAccountUsers != null && currentAccountUsers > maxAccountUsers) {
    return {
      icon: "bx-group",
      title: "Cari Kullanıcı Limitiniz Aşıldı",
      message: `Mevcut paketiniz en fazla ${maxAccountUsers} cari kullanıcı destekliyor, ancak ${currentAccountUsers} cari kullanıcınız bulunuyor. Yeni cari kullanıcı ekleyebilmek için paketinizi yükseltmeniz gerekmektedir.`,
      cta: "Paketi Yükseltin",
    }
  }

  return null
}

const LIMIT_DISMISS_KEY = "planLimitWarningDismissed"

const readLimitDismissed = () => {
  try {
    return sessionStorage.getItem(LIMIT_DISMISS_KEY) === "true"
  } catch (e) {
    return false
  }
}

const SubscriptionExpiredOverlay = () => {
  const [limitDismissed, setLimitDismissed] = useState(readLimitDismissed)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentStatus, paymentRequired } = useSelector((state) => state.subscription)
  const { roles } = useAuth()

  const dismissLimit = () => {
    try {
      sessionStorage.setItem(LIMIT_DISMISS_KEY, "true")
    } catch (e) {
      // Saklanamazsa uyarı yalnız bu sayfa ömrü boyunca kapalı kalır.
    }
    setLimitDismissed(true)
  }

  const handleNavigate = (path) => {
    if (paymentRequired) dispatch(clearPaymentRequired())
    navigate(path)
  }

  // SUPER_ADMIN için gösterme
  if (roles.includes("ROLE_SUPER_ADMIN")) return null

  // Süper adminden şirkete geçişte (impersonation) gösterme
  const isImpersonating =
    currentStatus?.impersonating ||
    localStorage.getItem("impersonationActive") === "true"
  if (isImpersonating) return null

  // EXPIRED tüm ekran deneyimini SubscriptionGate yönetir (blur + kilit kartı).
  // Overlay bu durumda KOŞULSUZ susar — aksi halde bir 402 tetiklendiğinde
  // gate kartının önünde ikinci (koyu backdrop'lı) bir popup çıkıp çakışır.
  if (currentStatus?.status === "EXPIRED") return null
  // Ücretsiz pakete düşüş bildirimi önce gösterilir; limit uyarısı onun arkasına binmesin.
  if (currentStatus?.freeFallbackFromPlanName) return null
  if (!paymentRequired && limitDismissed) return null

  const content = getOverlayContent(currentStatus, paymentRequired)
  if (!content) return null

  // Abonelik kilidinde paket kararı firma admininde; diğer roller satın alma ekranına gidemez.
  const waitsForAdmin = Boolean(paymentRequired?.reason) && !roles.includes("ROLE_COMPANY_ADMIN")

  // Abonelik sayfalarına erişime izin ver
  if (isSubscriptionAllowedPath(location.pathname)) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        zIndex: 1040,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "1rem",
      }}
    >
      <SubscriptionLockedCard
        content={waitsForAdmin ? { ...content, message: ADMIN_DECISION_MESSAGE } : content}
        onPrimary={waitsForAdmin ? undefined : () => handleNavigate("/subscription/plans")}
        onSecondary={waitsForAdmin ? undefined : () => handleNavigate("/subscription/history")}
        onClose={paymentRequired ? () => dispatch(clearPaymentRequired()) : dismissLimit}
      />
    </div>
  )
}

export default SubscriptionExpiredOverlay
